import { RecoveryLink } from "./AuthRecovery"
import { useSignInWithEmailAndPassword } from "@/api/authQueries"
import { Link, useNavigate } from "react-router-dom"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { APP_ROUTES } from "@/common/urls"
import { CheckCheck, Loader } from "lucide-react"

const LoginFormSchema = z.object({
    email: z.string().email({ message: "Please enter a valid email address" }),
    password: z.string().min(2, { message: "Password is required" }),
})

export default function AuthLogin() {
    const navFn = useNavigate()
    const {
        mutate: signInWithEmailPassword,
        isPending,
        isSuccess,
    } = useSignInWithEmailAndPassword(navFn)

    const form = useForm<z.infer<typeof LoginFormSchema>>({
        resolver: zodResolver(LoginFormSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    })

    function onSubmit(values: z.infer<typeof LoginFormSchema>) {
        signInWithEmailPassword(values)
    }
    return (
        <div className="grid place-items-center bg-center min-h-[calc(100dvh_-_theme(spacing.48))] w-full bg-no-repeat bg-auth-pattern pb-20 pt-8 px-4">
            <div className="bg-white shadow-lg border-input w-full max-w-[645px] rounded-lg py-8 sm:py-10 px-6 sm:px-16 md:px-32 gap-4 sm:gap-6 flex flex-col justify-center items-center">
                <section className="flex items-center justify-center w-full">
                    <img
                        src="/CMHT_logo_large.svg"
                        alt="CMHT Logo"
                        className="max-w-full h-auto"
                    />
                </section>

                <section className="flex flex-col gap-6 items-center text-center">
                    <h1 className="text-2xl text-app-danger font-bold">
                        Welcome
                    </h1>
                    <p className="text-sm">
                        Please enter your account details below
                    </p>
                </section>
                <section className="flex flex-col w-full">
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="flex flex-col gap-10"
                        >
                            <div className="gap-3 flex flex-col">
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Email Address</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="rounded-lg border-black"
                                                    placeholder="Email"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Password</FormLabel>
                                            <FormControl>
                                                <Input
                                                    className="rounded-lg border-black"
                                                    type="password"
                                                    placeholder="Password"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Link
                                    className="text-app-defaultLight underline font-bold w-full text-right text-sm"
                                    to={APP_ROUTES.AUTH.FORGOT_PASSWORD}
                                >
                                    Forgot Password?
                                </Link>
                            </div>
                            <Button
                                disabled={
                                    isPending ||
                                    isSuccess ||
                                    Object.keys(form.formState.errors).length >
                                        0
                                }
                                size="lg"
                                type="submit"
                                className="flex justify-center items-center gap-4 disabled:cursor-not-allowed w-full"
                            >
                                <span>Submit</span>
                                {isPending && (
                                    <Loader
                                        size={24}
                                        className="animate-spin"
                                    />
                                )}
                                {isSuccess && (
                                    <CheckCheck
                                        size={24}
                                        className="animate-pulse"
                                    />
                                )}
                            </Button>
                        </form>
                    </Form>
                </section>
                <RecoveryLink />
                <section className="text-sm">
                    <span>New user?</span>{" "}
                    <Link
                        className="text-app-defaultLight underline font-bold"
                        to={APP_ROUTES.AUTH.REGISTER}
                    >
                        Sign up here
                    </Link>
                </section>
            </div>
        </div>
    )
}
